import MSearchResults from './m-search-results.js';

class MSearch {
	#endpoint = '/api/search';

	#fetchController = null;

	/** @param {HTMLElement} element */
	constructor(element) {
		this.element = element;
		/** @type {HTMLInputElement} */
		this.inputElement = element.querySelector('input[type="search"]');
		this.headerElement = element.closest('.o-header');
		this.searchResults = new MSearchResults(this.headerElement.querySelector('.m-search-results'));
		this.containerElement = document.querySelector('.o-header__search');
		this.loadingIndicator = element.querySelector('.a-icon[data-variant="loading"]');

		// Event Listeners
		this.inputElement.addEventListener('focus', () => {
			this.hiderOtherPopovers();
			this.searchResults.show();
		});
		this.containerElement.querySelectorAll('a, input, button').forEach((focusElement) => {
			focusElement.addEventListener('blur', (/** @type {FocusEvent} */ event) => {
				if (!event.relatedTarget?.closest('.o-header__search') && event.relatedTarget !== null) {
					this.searchResults.hide();
					// focus first nav element
					this.headerElement.querySelector('.o-header__mainnav :where(a, button)')?.focus();
				}
			});
		});
		this.inputElement.addEventListener('input', () => {
			this.hiderOtherPopovers();
			this.searchResults.show();
			this.search(this.inputElement.value);
		});

		window.addEventListener('load', () => {
			this.setSearchResultsPadding();
		});
		window.addEventListener('resize', () => {
			this.setSearchResultsPadding();
		});

		document.addEventListener('keydown', (event) => {
			// Check for Cmd + K (Mac) or Ctrl + K (Windows/Linux)
			if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
				if (this.searchResults.isOpen) {
					this.searchResults.hide();
				} else {
					this.searchResults.show();
					this.inputElement.focus();
				}
				event.preventDefault(); // Prevent default action of the key combination
			}
		});

		this.containerElement.addEventListener('toggle', (/** @var {ToggleEvent} */ event) => {
			if (event.newState === 'open') {
				this.inputElement.focus();
			}
			if (event.newState === 'closed') {
				this.searchResults.hide();
			}
		});

		// Init
		this.setSearchResultsPadding();
	}

	set loading(value) {
		this.loadingIndicator.toggleAttribute('hidden', !value);
	}

	get loading() {
		return !this.loadingIndicator.getAttribute('hidden');
	}

	/** @param {string} query */
	async search(query) {
		this.loading = true;
		this.#fetchController?.abort();

		if (query.length >= 3) {
			const params = new URLSearchParams({ q: query.toLowerCase() });
			try {
				this.#fetchController = new AbortController();

				const response = await fetch(`${this.#endpoint}?${params.toString()}`, {
					signal: this.#fetchController.signal,
				});
				/** @type {ApiResponse} */
				const data = await response.json();
				if (data.status === 'ok' || data.status === 'info') {
					if (data.status === 'ok') {
						this.renderResults(data.data, query);
					} else {
						this.showSearchNotice(data.message);
					}

					try {
						window._paq?.push(['trackSiteSearch', query, null, data.data?.count]);
					} catch (error) {
						console.info('tracking error', error);
					}
				} else {
					this.showSearchNotice(data.message ?? 'Fatal error');
				}
				this.loading = false;
			} catch (/** @type {Error} */ error) {
				// Error 20 is AbortError an comes from this.#fetchController.abort().
				// NetworkError appears in Firefox, when user submits form while results are fetched.
				if (!(error.name === 'AbortError' || error.message === 'NetworkError when attempting to fetch resource.')) {
					this.loading = false;
					this.showSearchNotice(error.message);
					console.info(error);
				}
			}
		} else {
			this.loading = false;
			this.renderResults();
		}
	}

	showSearchNotice(message, theme = null) {
		/** @todo */
		this.searchResults.renderNotice(message, theme);
		console.info(message, theme);
	}

	/** @param {?SearchData} data */
	/** @param {?string} query */
	renderResults(data = null, query = null) {
		this.searchResults.render(data, query);
	}

	hiderOtherPopovers() {
		this.headerElement.querySelectorAll('[data-popover-id][data-stack="1"]:not(.m-search-results)')
			.forEach((_) => _.toggleAttribute('hidden', true));
	}

	setSearchResultsPadding() {
		const headerHeight = this.headerElement.querySelector('.o-header__center').offsetHeight;
		const {
			offsetHeight,
			offsetTop,
		} = this.element;
		const marginBlockStart = headerHeight - offsetHeight - offsetTop;
		this.searchResults.setMarginBlockStart(marginBlockStart * -1);
	}
}

document.querySelectorAll('.m-search').forEach((_) => new MSearch(_));
